import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { FaLock, FaTimes } from 'react-icons/fa'
import CreatableSelect from 'react-select/creatable'
import { TableColumn } from 'react-data-table-component'
import useFetch from '../../../../_fetch'
import { currencies } from '../../../../_utils'
import { IPartnership, PartnershipType } from '../types'
import { useAuth } from '../../../../auth/auth-context'
import { ClickDataTable } from '../click-table'

type PartnershipFormState = {
    name: string
    type: PartnershipType
    value: string
    currency: string
    startDate: string
    expiryDate: string
    allowCookielessConversions: boolean
    cookieDays: string
}

function getTodayDateString() {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function createInitialForm(): PartnershipFormState {
    return {
        name: '',
        type: 'sale',
        value: '',
        currency: 'DKK',
        startDate: getTodayDateString(),
        expiryDate: '',
        cookieDays: '30',
        allowCookielessConversions: false,
    }
}

function stripCommonSubdomain(hostname: string) {
    return hostname.replace(/^www\d*\./i, '')
}

function cleanDomain(input: string) {
    try {
        const url = input.startsWith('http') ? input : `https://${input}`
        const parsed = new URL(url)
        return stripCommonSubdomain(parsed.hostname.toLowerCase())
    } catch {
        return null
    }
}

function isValidDomain(domain: string) {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
}

function getValueLabel(type: PartnershipType) {
    if (type === 'sale') return 'Sale percentage'
    if (type === 'click') return 'Value per click'
    return 'Value'
}

function getTypeBadgeVariant(type: PartnershipType) {
    if (type === 'sale') return 'primary'
    if (type === 'click') return 'info'
    return 'warning'
}

function formatPayout(partnership: IPartnership) {
    if (partnership.type === 'sale') return `${partnership.value}%`
    if (partnership.type === 'click') return `${partnership.value} ${partnership.currency} per click`
    return `${partnership.value} ${partnership.currency} per lead`
}

function getApiErrorMessage(err: any, fallback: string) {
    if (typeof err !== 'string') return fallback

    try {
        const parsed = JSON.parse(err)
        if (typeof parsed?.error === 'string' && parsed.error.trim()) return parsed.error
        if (typeof parsed?.message === 'string' && parsed.message.trim()) return parsed.message
    } catch {
        // Use plain text below.
    }

    return err.trim() || fallback
}

const LinkScriptPartnerships: React.FC = () => {
    const navigate = useNavigate()
    const { doFetch } = useFetch()
    const { user } = useAuth()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [modalError, setModalError] = useState('')
    const [success, setSuccess] = useState('')
    const [partnerships, setPartnerships] = useState<IPartnership[]>([])
    const [form, setForm] = useState<PartnershipFormState>(createInitialForm)
    const [formDomains, setFormDomains] = useState<string[]>([])
    const [showModal, setShowModal] = useState(false)
    const [domainError, setDomainError] = useState('')

    const valueLabel = useMemo(() => getValueLabel(form.type), [form.type])

    const loadPartnerships = useCallback(async () => {
        try {
            setLoading(true)
            setError('')
            const res = await doFetch('/user/partnerships')
            if (res.status >= 400) throw await res.text()
            const data = await res.json()
            setPartnerships(Array.isArray(data) ? data : [])
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Could not load partnerships'))
        } finally {
            setLoading(false)
        }
    }, [doFetch])

    useEffect(() => {
        loadPartnerships()
    }, [loadPartnerships])

    const updateForm = (key: keyof PartnershipFormState, value: string) => {
        if (key === 'type') {
            setForm((prev) => {
                const nextType = value as PartnershipType
                if (nextType === 'click') {
                    return { ...prev, type: nextType, cookieDays: '0' }
                }
                if (nextType === 'sale') {
                    return {
                        ...prev,
                        type: nextType,
                        currency: 'DKK',
                        cookieDays: prev.cookieDays === '0' ? '30' : prev.cookieDays,
                    }
                }
                return {
                    ...prev,
                    type: nextType,
                    cookieDays: prev.cookieDays === '0' ? '30' : prev.cookieDays,
                }
            })
            return
        }

        setForm((prev) => ({ ...prev, [key]: value }))
    }

    const resetForm = () => {
        setForm(createInitialForm())
        setFormDomains([])
        setDomainError('')
    }

    const openCreateModal = () => {
        setShowModal(true)
        setError('')
        setModalError('')
        setSuccess('')
        resetForm()
    }

    const closeModal = () => {
        setShowModal(false)
        setModalError('')
        resetForm()
    }

    const addDomain = (option: any) => {
        const raw = option?.value?.trim()?.toLowerCase()
        if (!raw) return

        const domain = cleanDomain(raw)
        if (!domain || !isValidDomain(domain)) {
            setDomainError('Invalid domain')
            return
        }

        setDomainError('')
        setFormDomains((current) => (current.includes(domain) ? current : [...current, domain]))
    }

    const removeDomain = (domain: string) => {
        setFormDomains((current) => current.filter((entry) => entry !== domain))
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setModalError('')
        setSuccess('')

        if (!form.name.trim()) {
            setModalError('Partner name is required')
            return
        }

        if (!form.startDate) {
            setModalError('Start date is required')
            return
        }

        const valueNumber = Number(form.value)
        if (!Number.isFinite(valueNumber) || valueNumber < 0) {
            setModalError('Value must be zero or greater')
            return
        }

        if (form.expiryDate && form.expiryDate < form.startDate) {
            setModalError('End date cannot be before start date')
            return
        }

        const cookieDays = form.type === 'click' ? 0 : Number(form.cookieDays)
        if (form.type !== 'click' && (!Number.isInteger(cookieDays) || cookieDays <= 0)) {
            setModalError('Cookie duration must be at least 1 day')
            return
        }
        if (form.type !== 'click' && cookieDays > 60) {
            setModalError('Cookie duration cannot exceed 60 days')
            return
        }

        try {
            setSaving(true)

            const payload = {
                name: form.name,
                domains: formDomains,
                type: form.type,
                value: valueNumber,
                currency: form.type === 'sale' ? 'DKK' : form.currency,
                startDate: form.startDate,
                expiryDate: form.expiryDate || null,
                cookieDays,
                allowCookielessConversions: form.type === 'sale' && form.allowCookielessConversions,
            }

            const res = await doFetch('/user/partnerships', payload)
            if (res.status >= 400) throw await res.text()

            const saved = await res.json()
            setPartnerships((prev) => [saved, ...prev])
            setSuccess('Partnership created')
            closeModal()
        } catch (err: any) {
            setModalError(getApiErrorMessage(err, 'Could not save partnership'))
        } finally {
            setSaving(false)
        }
    }

    const visibleLifecycle = (partnership: IPartnership) => {
        if (partnership.lifecycle === 'deleted') return 'deleted'
        if (partnership.lifecycle === 'archived') return 'archived'
        if (user?.kind === 'advertiser' && partnership.lifecycle === 'awaiting_publisher') return 'awaiting_publisher'
        return partnership.lifecycle === 'expired' ? 'expired' : partnership.lifecycle === 'scheduled' ? 'scheduled' : 'active'
    }
    const sortedPartnerships = [...partnerships].sort((first, second) => {
        const lifecycleOrder = (partnership: IPartnership) => partnership.lifecycle === 'archived' || partnership.lifecycle === 'deleted' ? 2 : partnership.lifecycle === 'expired' ? 1 : 0
        const firstOrder = lifecycleOrder(first)
        const secondOrder = lifecycleOrder(second)
        if (firstOrder !== secondOrder) return firstOrder - secondOrder
        return Number(second.id.split('-')[0]) - Number(first.id.split('-')[0])
    })
    const lifecycleSortRank = (partnership: IPartnership) => {
        const lifecycle = visibleLifecycle(partnership)
        if (lifecycle === 'archived' || lifecycle === 'deleted') return 0
        if (lifecycle === 'expired') return 1
        if (lifecycle === 'scheduled') return 2
        if (lifecycle === 'awaiting_publisher') return 3
        return 4
    }
    const columns: TableColumn<IPartnership>[] = [
        {
            name: 'Partner',
            selector: (partnership) => partnership.name,
            sortable: true,
            minWidth: '220px',
            grow: 2,
            cell: (partnership) => <div className="py-1">
                <div className="fw-semibold d-flex align-items-center gap-1">
                    {partnership.name}
                    {partnership.locked && <FaLock title="Locked by advertiser" className="text-muted" style={{ fontSize: '0.72rem' }} />}
                </div>
                <div className="text-muted small text-truncate" style={{ maxWidth: 320 }}>
                    {partnership.domains.length ? partnership.domains.join(', ') : 'No domains set'}
                </div>
            </div>,
        },
        {
            name: 'Type',
            selector: (partnership) => partnership.type,
            sortable: true,
            width: '105px',
            cell: (partnership) => <Badge bg={getTypeBadgeVariant(partnership.type)}>{partnership.type.charAt(0).toUpperCase() + partnership.type.slice(1)}</Badge>,
        },
        {
            name: 'Payout',
            selector: (partnership) => partnership.value,
            sortable: true,
            minWidth: '150px',
            cell: (partnership) => formatPayout(partnership),
        },
        {
            name: user?.kind === 'advertiser' ? 'Publisher' : 'Advertiser',
            selector: (partnership) => user?.kind === 'advertiser' ? partnership.publisher_users[0]?.username || '' : partnership.advertiser?.username || '',
            sortable: true,
            minWidth: '145px',
            cell: (partnership) => user?.kind === 'advertiser'
                ? partnership.publisher_users[0]?.username || <span className="text-muted">—</span>
                : partnership.advertiser?.username || <span className="text-muted">—</span>,
        },
        {
            name: 'Period',
            selector: (partnership) => partnership.startDate,
            sortable: true,
            minWidth: '210px',
            cell: (partnership) => <span className="small text-nowrap">{partnership.startDate} <span className="text-muted">→</span> {partnership.expiryDate || 'No end date'}</span>,
        },
        {
            name: 'Status',
            id: 'partnershipSort',
            selector: (partnership) => visibleLifecycle(partnership) || 'active',
            sortable: true,
            sortFunction: (first, second) => {
                const rankDifference = lifecycleSortRank(first) - lifecycleSortRank(second)
                if (rankDifference !== 0) return rankDifference
                return Number(first.id.split('-')[0]) - Number(second.id.split('-')[0])
            },
            minWidth: '130px',
            cell: (partnership) => <div>
                <Badge bg={visibleLifecycle(partnership) === 'active' ? 'success' : visibleLifecycle(partnership) === 'expired' || visibleLifecycle(partnership) === 'archived' || visibleLifecycle(partnership) === 'deleted' ? 'secondary' : 'warning'}>
                    {(visibleLifecycle(partnership) || 'active').replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())}
                </Badge>
                {partnership.permissions?.isLegacyOwnershipAmbiguous && <div className="text-muted small mt-1">Admin managed</div>}
            </div>,
        },
        {
            name: 'Actions',
            ignoreRowClick: true,
            width: '100px',
            cell: (partnership) => <Button variant="outline-dark" size="sm" onClick={() => navigate(`/partnerships/${partnership.id}/overview`)}>View</Button>,
        },
    ]

    return (
        <div className="container-fluid px-2 px-md-3 partnership-page-shell">
            {!showModal && error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <div className="partnership-page-header mb-4">
                <div>
                    <h3 className="mb-1">Partnerships</h3>
                    <div className="text-muted">
                        {user?.kind === 'advertiser'
                            ? 'Create direct partnerships and review performance across your publishers.'
                            : 'Create and manage direct partner agreements for sale and click payouts.'}
                    </div>
                </div>
                <Button onClick={openCreateModal}>Create partnership</Button>
            </div>

            <hr className="mb-4" />

            {loading ? (
                <Card className="border-0 shadow-sm"><Card.Body className="d-flex justify-content-center py-5"><Spinner animation="border" /></Card.Body></Card>
            ) : !sortedPartnerships.length ? (
                <Card className="border-0 shadow-sm"><Card.Body className="text-center py-5"><div className="fw-semibold mb-1">No partnerships yet</div><div className="text-muted">Create your first partnership to get started.</div></Card.Body></Card>
            ) : (
                <ClickDataTable
                    title="Partnership overview"
                    columns={columns}
                    data={sortedPartnerships}
                    id="partnershipSort"
                    height="560px"
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[25, 50, 100]}
                    showExportActions={false}
                    pointerOnHover
                    onRowClicked={(partnership) => navigate(`/partnerships/${partnership.id}/overview`)}
                    customStyles={{ rows: { style: { minHeight: '58px' } } }}
                />
            )}

            <Modal show={showModal} onHide={closeModal} size="lg">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>Add partnership</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {modalError && <Alert variant="danger">{modalError}</Alert>}
                        {domainError && <Alert variant="danger">{domainError}</Alert>}
                        {user?.kind === 'advertiser' && (
                            <Alert variant="info">The partnership will be created in “Awaiting publisher” status. An admin can attach publishers afterwards.</Alert>
                        )}

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="partnership-name">
                                    <Form.Label>Partner name</Form.Label>
                                    <Form.Control
                                        value={form.name}
                                        onChange={(event) => updateForm('name', event.target.value)}
                                        placeholder="Example Partner"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3" controlId="partnership-domains">
                                    <Form.Label>Partner domains</Form.Label>
                                    <CreatableSelect
                                        inputId="partnership-domains"
                                        placeholder="Add domain (e.g. partner.com)"
                                        isClearable
                                        formatCreateLabel={(value) => `Add "${value}"`}
                                        onChange={addDomain}
                                        onCreateOption={(value) => addDomain({ value })}
                                    />
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {formDomains.map((domain) => (
                                            <Badge key={domain} bg="info" className="d-flex align-items-center px-2 py-1">
                                                {domain}
                                                <FaTimes
                                                    className="ms-2"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => removeDomain(domain)}
                                                />
                                            </Badge>
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3" controlId="partnership-type">
                                    <Form.Label>Type</Form.Label>
                                    <Form.Select
                                        value={form.type}
                                        onChange={(event) => updateForm('type', event.target.value as PartnershipType)}
                                    >
                                        <option value="sale">Sale</option>
                                        <option value="click">Click</option>
                                        <option value="lead" disabled>Lead</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            {form.type !== 'sale' ? (
                                <Col md={4}>
                                    <Form.Group className="mb-3" controlId="partnership-currency">
                                        <Form.Label>Currency</Form.Label>
                                        <Form.Select
                                            value={form.currency}
                                            onChange={(event) => updateForm('currency', event.target.value)}
                                        >
                                            {currencies.map((currency) => (
                                                <option key={currency} value={currency}>
                                                    {currency}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            ) : null}
                            <Col md={4}>
                                <Form.Group className="mb-3" controlId="partnership-value">
                                    <Form.Label>{valueLabel}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={form.value}
                                        onChange={(event) => updateForm('value', event.target.value)}
                                        placeholder="0"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3" controlId="partnership-start-date">
                                    <Form.Label>Start date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={form.startDate}
                                        max={form.expiryDate || undefined}
                                        onChange={(event) => updateForm('startDate', event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3" controlId="partnership-end-date">
                                    <Form.Label>End date (optional)</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={form.expiryDate}
                                        min={form.startDate || undefined}
                                        onChange={(event) => updateForm('expiryDate', event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            {form.type !== 'click' && (
                                <Col md={4}>
                                    <Form.Group className="mb-3" controlId="partnership-cookie-days">
                                        <Form.Label>Cookie duration (days)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            max={60}
                                            value={form.cookieDays}
                                            onChange={(event) => updateForm('cookieDays', event.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        {form.type === 'sale' && <Form.Group className="mb-3">
                            <Form.Check id="create-cookieless-conversions" label="Allow conversions without a cookie" checked={form.allowCookielessConversions} onChange={(event) => setForm({ ...form, allowCookielessConversions: event.target.checked })} />
                            <Form.Text>Conversions without a prior affiliate click are credited to this partnership’s publisher. Add the agreement ID to the confirmation-page setup shown in the integration guide.</Form.Text>
                        </Form.Group>}

                        {form.type === 'click' && (
                            <Alert variant="info" className="mb-0">
                                Click type works with link automation only.
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeModal}>Cancel</Button>
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Saving...' : 'Create partnership'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

        </div>
    )
}

export default LinkScriptPartnerships
